import { DataSourceTable } from '@mode/shared/data-access-algolia';
import { Table } from '@mode/shared/contract-common';
import helper from './helper';
import { createColumnIconClass } from './icon-provider';

/**
 * Private utillities
 */

/**
 * @param  {string} word input to find all indices of
 * @param  {Array<string>} collection tokens/words in an array
 * @returns Array numbers indicating position in colllection
 */
function getIndices(word: string, collection: Array<string>): Array<number> {
  return collection.reduce((prevValue, currentToken, index) => {
    if (currentToken === word) {
      return [...prevValue, index];
    } else {
      return prevValue;
    }
  }, []);
}

function identifyTableByAlias(alias: string, queryText: string): string {
  const sqlTokens = queryText.replace(/\n/g, ' ').split(' ');

  const fromIndices = getIndices('from', sqlTokens);
  const joinIndices = getIndices('join', sqlTokens);

  let suggestedTable = '';

  [...fromIndices, ...joinIndices].some((indexToCheck) => {
    const queryAfterFrom = sqlTokens.slice(indexToCheck + 1);
    // tableName AS t1 || tableName t1 -> [tableName, 'as', 't1'] || [tableName, 't1']
    const aliasDeclaration: Array<string> = queryAfterFrom
      .slice(0, queryAfterFrom.indexOf(alias) + 1)
      .filter((declaration) => declaration !== '');

    const declarationLength = aliasDeclaration.length;
    const hasMatchingTable =
      (declarationLength === 3 || declarationLength === 2) && aliasDeclaration[aliasDeclaration.length - 1] === alias;

    if (hasMatchingTable) {
      [suggestedTable] = aliasDeclaration;
      return true;
    } else {
      return false;
    }
  });

  return suggestedTable;
}

function fetchColumnsByTable(tableName: string) {}

function getSuggestedTable(prefix: string, session: AceAjax.IEditSession, cursorPosition: AceAjax.Position): string {
  const queryText = session.getValue().toLowerCase();
  let identifiedTable = '';
  if (prefix.includes('.')) {
    const [alias] = prefix.split('.');
    identifiedTable = identifyTableByAlias(alias, queryText);
  } else {
    // look ahead to get table name
    const isTableAhead = session.doc
      .getAllLines()
      .slice(cursorPosition.row)
      .some((line: string, index: number) => {
        // looks at the right side of query
        // select cre from myTable -> myTable
        const isWithinOtherSelect = line.match(/select/i) !== null && index !== 0;
        const { index: fromIndex } = line.match(/from|join/i) || { index: -1 };
        const hasMatchingTable = fromIndex >= 0 && !isWithinOtherSelect;
        if (hasMatchingTable) {
          [, identifiedTable = ''] = line.slice(fromIndex).split(' ');
          return true;
        } else {
          identifiedTable = '';
          return false;
        }
      });
    // look behind for where clause??
    const queryFromBackwards: string = session
      .getTextRange({
        start: { row: 0, column: 0 },
        end: cursorPosition,
      } as AceAjax.Range)
      .replace(/\s\s+/g, ' ');

    const currentQuery = queryFromBackwards.substring(queryFromBackwards.toLowerCase().lastIndexOf('select'));
    const { index: tableIndex } = currentQuery.match(/from|join/i) || {
      index: -1,
    };

    if (tableIndex >= 0 && !isTableAhead) {
      [, identifiedTable = ''] = currentQuery.slice(tableIndex).split(' ');
    }
  }

  return identifiedTable;
}

function combineDuplicateIfExists(sourceArray: Array<AceAjax.Completion>): Array<AceAjax.Completion> {
  const uniqueColumns: { [key: string]: AceAjax.Completion } = {};

  sourceArray.forEach((currentColumn) => {
    if (uniqueColumns[currentColumn.value]) {
      uniqueColumns[currentColumn.value].meta = 'multiple';
    } else {
      // fix for https://app.asana.com/0/773998528568598/873479105015243/f
      // truncate longer than needed meta -> tablenames
      // to prevent overalps with column suggestion
      currentColumn.meta = helper.truncate(currentColumn.meta);
      uniqueColumns[currentColumn.value] = currentColumn;
    }
  });
  return (<any>Object).values(uniqueColumns);
}

/**
 * This method converts Algolia column response into Ace Completion Object
 */
function convertToAceCompletion(columnFromAlgolia, meta = ''): AceAjax.Completion {
  const MAX_COLUMN_LABEL_LENGTH = 45;
  // [TODO] [FIXME] If column lable needs to be decoded, we should to enclose
  // this decoded lable either in signel/double qoutes
  const columnLabel = decodeURIComponent(columnFromAlgolia.name.value.replace(/<[^>]*>/g, ''));
  const {
    dataType: { value: columnType },
  } = columnFromAlgolia;
  // aloglia adds <em> </em> tags to highlight dataType
  const columnCompletion: AceAjax.Completion = {
    value: helper.truncate(columnLabel, MAX_COLUMN_LABEL_LENGTH),
    highlighted: columnFromAlgolia.name.value,
    icon: createColumnIconClass(columnType.replace(/<[^>]*>/g, '')),
    score: -1, //Calculate at the callsite
    replacement: columnLabel,
    suggestionType: 'column',
    completer: helper.getCompleter(),
    meta,
  };

  return columnCompletion;
}

function getColumnsFromTable(table): Array<AceAjax.Completion> {
  let {
    fullName: tableNameWithSchema,
    _highlightResult: { columns },
    name: tableName,
  } = table;

  // If columns aren't present on _highlightResult, the response is from DataIndex rather than Algolia
  if (!columns && 'columns' in table) {
    columns = table.columns.map(({ name, dataType }) => ({
      dataType: { value: dataType },
      name: { value: name },
    }));
  }

  return columns.reduce((matchedColumns, currentColumn, currentIndex) => {
    if (currentColumn.name.matchLevel === 'full') {
      const newlyMatchedColumn = Object.assign(
        convertToAceCompletion(currentColumn, tableName),
        helper.getCompletionScore(currentColumn, currentIndex, columns.length - 1),
        {
          infoTitle: tableNameWithSchema,
        }
      );
      return [...matchedColumns, newlyMatchedColumn];
    } else {
      return matchedColumns;
    }
  }, []);
}
// Algolia table index contains matched columns
// This method extracts columns from all matched results in algolia
function getAllColumnsFromSchema(responseFromAlgolia: Array<any>): Array<AceAjax.Completion> {
  return responseFromAlgolia.reduce(
    (columnsFromPrevTables, currentTable) => [...columnsFromPrevTables, ...getColumnsFromTable(currentTable)],
    []
  );
}

function parseMatchedColumnsBy(dataFromAlgolia, indexedBy: 'table' | 'dataSourceTable'): Array<AceAjax.Completion> {
  return combineDuplicateIfExists(getAllColumnsFromSchema(dataFromAlgolia[indexedBy]));
}

function getColumnsInTable(tableResponse): AceAjax.Completion[] {
  if (tableResponse === '') {
    return [];
  } else {
    let {
      fullName: tableNameWithSchema,
      _highlightResult: { columns },
      name: tableName,
    } = tableResponse;

    // If columns aren't present on _highlightResult, the response is from DataIndex rather than Algolia
    if (!columns && 'columns' in tableResponse) {
      columns = tableResponse.columns.map(({ name, dataType }) => ({
        dataType: { value: dataType },
        name: { value: name },
      }));
    }

    return columns.map((column, index) =>
      Object.assign(
        convertToAceCompletion(column, tableName),
        helper.getCompletionScore(column, index, columns.length - 1),
        {
          infoTitle: tableNameWithSchema,
        }
      )
    );
  }
}

function getColumnInfoForTableTooltip(table: Table | DataSourceTable): {
  value: string;
  icon: string;
}[] {
  return getColumnsInTable(table)
    .slice(0, 4)
    .map((column) => ({
      value: column.value,
      icon: column.icon,
    }));
}

export default {
  fetchColumnsByTable,
  parseMatchedColumnsBy,
  getSuggestedTable,
  getColumnsInTable,
  getColumnInfoForTableTooltip,
};
